/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Col,
  Spin,
  Modal,
  Input,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import TextArea from 'antd/lib/input/TextArea';
import { checkToken } from 'api/utils';
import Select from 'components/Select';
import Table from 'components/Table';
import Uploader from 'components/Uploader';
import Previewer from 'components/Previewer';
import FormSubmitControls from 'components/FormSubmitControls';
import { onError } from 'utils/handlers';
import {
  getLiquidacionesEmpleadosAnexos,
  patchLiquidacionEmpleadoAnexo,
} from 'api/nominas/liquidaciones/LiquidacionesEmpleadosAnexos';

export const liquidacionEmpleadoAnexoPermission = {
  permissionModel: 'liquidacionempleadoanexo',
  catalogsModels: [],
  tabsModels: [],
};

const nombreDeArchivo = (archivo) => {
  if (!archivo || typeof archivo !== 'string') return '';
  return decodeURIComponent(archivo.split('?')[0].split('/').pop());
};

const esImagen = (file) => !!file?.archivo && /\.(jpg|jpeg|png|gif|bmp|svg|webp)(\?.*)?$/i.test(file.archivo);

/**
 * Tabla de anexos de una Liquidación de Empleado.
 * `esOtroDocumento` = false → Documentos Generales y Específicos.
 * `esOtroDocumento` = true  → Otros Documentos (con columna Aplica).
 */
const TablaAnexos = ({
  data,
  esOtroDocumento,
  permission,
  loading,
  setLoading,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [currentFile, setCurrentFile] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setOpen(false);
    form.resetFields();
  };

  const onClickEdit = () => {
    const [key] = selectedRowKeys;
    const match = data.find((item) => item.id === key);
    if (!match) return;
    form.resetFields();
    form.setFieldsValue({ ...match, archivo: match.archivo });
    setOpen(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const [key] = selectedRowKeys;
      const values = { ...form.getFieldsValue() };
      // `archivo` llega como URL cuando no se seleccionó uno nuevo: no se reenvía
      if (typeof values.archivo === 'string') delete values.archivo;
      delete values.documento;
      values.observaciones = values.observaciones || '';
      delete values.tipo;
      const saved = await patchLiquidacionEmpleadoAnexo(key, values);
      if (saved) {
        onCancel();
        await onRefresh();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, [form]);
    }
  };

  const marcarEntregado = async (recibido) => {
    try {
      setLoading(true);
      await Promise.all(selectedRowKeys.map((id) => patchLiquidacionEmpleadoAnexo(id, { recibido })));
      onCancel();
      await onRefresh();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickPreview = () => {
    const [key] = selectedRowKeys;
    setCurrentFile(data.find((e) => e.id === key));
    setVisiblePreview(true);
  };

  const handleOnRowClick = (record) => {
    if (selectedRowKeys.includes(record.id)) {
      setSelectedRowKeys(selectedRowKeys.filter((key) => key !== record.id));
    } else {
      setSelectedRowKeys([...selectedRowKeys, record.id]);
    }
  };

  // Selección de todos sobre el dataset completo (no solo la página actual)
  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    columnTitle: (
      <input
        type="checkbox"
        checked={!!data?.length && selectedRowKeys.length === data.length}
        onChange={(e) => setSelectedRowKeys(e.target.checked ? data.map((row) => row.id) : [])}
      />
    ),
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  const selectedOne = selectedRowKeys.length === 1 ? data.find((e) => e.id === selectedRowKeys[0]) : null;

  const columns = [
    {
      titleText: 'Documento',
      dataIndex: 'documento',
      key: 'documento',
      width: 300,
    },
    {
      titleText: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 150,
    },
    ...(esOtroDocumento ? [{
      titleText: 'Aplica',
      dataIndex: 'aplica',
      key: 'aplica',
      width: 100,
      render: (val) => (val ? 'Sí' : 'No'),
    }] : []),
    {
      titleText: 'Entregado',
      dataIndex: 'recibido',
      key: 'recibido',
      width: 120,
      render: (val) => (val ? 'Sí' : 'No'),
    },
    {
      titleText: 'Observaciones',
      dataIndex: 'observaciones',
      key: 'observaciones',
      width: 250,
    },
    {
      titleText: 'Archivo',
      dataIndex: 'archivo',
      key: 'archivo',
      width: 250,
      render: (val) => nombreDeArchivo(val),
    },
  ];

  return (
    <>
      <Table
        customActions={[
          {
            onClick: () => marcarEntregado(true),
            text: 'Marcar como Entregado',
            type: 'primary',
            disabled: !selectedRowKeys.length,
          },
          {
            onClick: () => marcarEntregado(false),
            text: 'Marcar como no Entregado',
            danger: true,
            disabled: !selectedRowKeys.length,
          },
          {
            onClick: onClickPreview,
            icon: <EyeOutlined />,
            text: 'Ver Documento',
            disabled: !selectedOne?.archivo,
          },
        ]}
        cols={columns}
        data={data}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={{
          onClickEdit,
        }}
        allowMultiFalseChanger={false}
        allowMultiTrueChanger={false}
        allowDoubleClick={false}
        permission={permission}
        allowExportPDF={false}
      />
      <Modal
        open={open}
        closable={false}
        maskClosable={!loading}
        keyboard={!loading}
        footer={null}
        title={(
          <FormSubmitControls
            label={`Editar ${esOtroDocumento ? 'Otro Documento' : 'Documento'}`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        onCancel={onCancel}
        forceRender
        width="35%"
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          name={esOtroDocumento ? 'otros' : 'generales'}
        >
          <Row gutter={10}>
            <Col span={24}>
              <Form.Item name="documento" label="Documento">
                <Input disabled />
              </Form.Item>
              <Form.Item name="tipo" label="Tipo">
                <Input disabled />
              </Form.Item>
              {esOtroDocumento && (
                <Form.Item name="aplica" label="Aplica" initialValue={false}>
                  <Select trueFalse />
                </Form.Item>
              )}
              <Form.Item name="recibido" label="Entregado" initialValue={false}>
                <Select trueFalse />
              </Form.Item>
              <Uploader
                title="Documento"
                limitMB={5}
                file={form.getFieldValue('archivo')}
                formItemName="archivo"
                allowedExtensions={['jpg', 'png', 'jpeg', 'pdf']}
                allowPreview={false}
              />
              <Form.Item name="observaciones" label="Observaciones">
                <TextArea rows={3} allowClear />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      <Previewer
        open={visiblePreview}
        setVisible={setVisiblePreview}
        iFrameUrl={currentFile?.archivo ? `${currentFile.archivo}${currentFile.archivo.includes('?') ? '&' : '?'}t=${new Date().getTime()}` : null}
        isImage={esImagen(currentFile)}
      />
    </>
  );
};

const LiquidacionesEmpleadosAnexos = ({ permission, liquidacionEmpleado }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesEmpleadosAnexos({ liquidacion_empleado: liquidacionEmpleado });
    setData(_data || []);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (!liquidacionEmpleado) {
      setData([]);
      return undefined;
    }
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const _data = await getLiquidacionesEmpleadosAnexos({ liquidacion_empleado: liquidacionEmpleado });
      if (mounted) {
        setData(_data || []);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, [liquidacionEmpleado]);

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <h4>Documentos Generales y Específicos</h4>
        <TablaAnexos
          permission={permission}
          loading={loading}
          setLoading={setLoading}
          onRefresh={fetchData}
          data={data.filter((e) => !e.es_otro_documento)}
          esOtroDocumento={false}
        />
        <h4 style={{ marginTop: 24 }}>Otros Documentos</h4>
        <TablaAnexos
          permission={permission}
          loading={loading}
          setLoading={setLoading}
          onRefresh={fetchData}
          data={data.filter((e) => e.es_otro_documento)}
          esOtroDocumento
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionesEmpleadosAnexos;
